import React, { useState, useEffect } from 'react';
import { useCloudAuth } from '../../context/CloudAuthContext';
import { useBusinessProfile } from '../../contexts/BusinessProfileContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
  Phone, 
  Save, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
  ExternalLink,
  Info,
  Webhook,
  Copy,
  Calendar,
  ShoppingBag,
  Package,
  Truck,
  Ban,
  Search,
  Clock,
  Link2,
  Plus,
  Trash2,
  Play,
  Loader2,
  PhoneForwarded,
  Building2
} from 'lucide-react';

export default function VoiceSettings() {
  const { fetchApi } = useCloudAuth();
  const { currentProfile, loading: profileLoading } = useBusinessProfile();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [copied, setCopied] = useState(null);
  const [activeTab, setActiveTab] = useState('twilio');

  // Twilio settings
  const [settings, setSettings] = useState({
    twilioAccountSid: '',
    twilioAuthToken: '',
    twilioPhoneNumber: '',
    twilioConnected: false,
    webhookUrl: '',
    webhookSecret: '',
    webhookEnabled: false,
    webhookEvents: ['call.completed']
  });

  // Integration states
  const [googleStatus, setGoogleStatus] = useState(null);
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [shopifyAuthMode, setShopifyAuthMode] = useState('oauth'); // 'credentials' | 'oauth'
  const [shopifyForm, setShopifyForm] = useState({
    storeName: '',
    clientId: '',
    clientSecret: '',
    adminAccessToken: '',
  });
  const [showShopifySecret, setShowShopifySecret] = useState(false);
  const [showShopifyToken, setShowShopifyToken] = useState(false);
  const [connectingShopify, setConnectingShopify] = useState(false);

  // Scheduling states
  const [schedulingConfig, setSchedulingConfig] = useState(null);

  // Custom API states
  const [apiIntegrations, setApiIntegrations] = useState([]);
  const [showAddApiModal, setShowAddApiModal] = useState(false);
  const [testingApiId, setTestingApiId] = useState(null);
  const [newApi, setNewApi] = useState({
    name: '',
    variableName: '',
    method: 'GET',
    url: '',
    headers: '',
    body: ''
  });

  // Voice Agent Tool Config (AI access toggles)
  const [toolConfig, setToolConfig] = useState({
    shopify: { enabled: false },
    googleCalendar: { enabled: false },
    customApi: { enabled: false }
  });

  // Multi-Phone Number Management
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState(null);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [purchasingNumber, setPurchasingNumber] = useState(false);
  const [savingPhoneSettings, setSavingPhoneSettings] = useState(false);
  const [newBusinessName, setNewBusinessName] = useState('');
  const [numberSearch, setNumberSearch] = useState({
    country: 'US',
    areaCode: '',
    contains: ''
  });

  // Phone-specific settings (for the selected phone)
  const [phoneSettings, setPhoneSettings] = useState({
    businessName: '',
    welcomeMessage: '',
    systemPrompt: '',
    voiceId: '',
    tone: 'friendly',
    callTransfer: {
      enabled: false,
      phoneNumber: ''
    }
  });

  const [testResult, setTestResult] = useState(null);

  // Reload settings when business profile changes
  useEffect(() => {
    if (currentProfile?.id) {
      // Reset phone selection when profile changes
      setSelectedPhoneId(null);
      setSelectedPhone(null);
      setPhoneSettings({
        businessName: '',
        welcomeMessage: '',
        systemPrompt: '',
        voiceId: '',
        tone: 'friendly',
        callTransfer: { enabled: false, phoneNumber: '' }
      });
      setAvailableNumbers([]);
      
      loadSettings();
    }
    
    // Check for Google OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      // Switch to integrations tab and show toast
      setActiveTab('integrations');
      toast.success('Google Calendar connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('google_error')) {
      setActiveTab('integrations');
      toast.error(`Google Calendar connection failed: ${params.get('google_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('shopify_connected') === 'true') {
      setActiveTab('integrations');
      toast.success('Shopify store connected successfully via OAuth!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('shopify_error')) {
      setActiveTab('integrations');
      const errMap = {
        hmac_verification_failed: 'Security check failed. Please try again.',
        token_exchange_failed: 'Could not exchange OAuth code for token. Check your Shopify app credentials.',
        token_validation_failed: 'Token received but validation with Shopify failed.',
        missing_code: 'OAuth code was not returned by Shopify.',
      };
      const raw = params.get('shopify_error');
      toast.error(`Shopify connection failed: ${errMap[raw] || raw}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [currentProfile?.id]);

  const loadSettings = async () => {
    if (!currentProfile?.id) return;
    
    try {
      setLoading(true);
      const profileParam = `businessProfileId=${currentProfile.id}`;
      const [settingsRes, googleRes, shopifyRes, schedulingRes, apiRes, toolConfigRes, phonesRes] = await Promise.all([
        fetchApi('/api/voice/settings'),
        fetchApi(`/api/cloud/integrations/calendar/google/status?${profileParam}`),
        fetchApi(`/api/cloud/integrations/shopify/status?${profileParam}`),
        fetchApi(`/api/cloud/integrations/scheduling/config?${profileParam}`),
        fetchApi(`/api/cloud/integrations/api-integrations?${profileParam}`),
        fetchApi(`/api/cloud/integrations/voice-agent/tools/config?${profileParam}`),
        fetchApi(`/api/voice/phone-numbers?${profileParam}`)  // Get phone numbers for this business profile
      ]);
      
      if (settingsRes.success && settingsRes.data) {
        setSettings(prev => ({
          ...prev,
          twilioAccountSid: settingsRes.data.twilioAccountSid || '',
          twilioAuthToken: settingsRes.data.twilioAuthToken || '',
          twilioPhoneNumber: settingsRes.data.twilioPhoneNumber || '',
          twilioConnected: settingsRes.data.twilioConnected || false,
          webhookUrl: settingsRes.data.webhookUrl || '',
          webhookSecret: settingsRes.data.webhookSecret || '',
          webhookEnabled: settingsRes.data.webhookEnabled || false,
          webhookEvents: settingsRes.data.webhookEvents || ['call.completed']
        }));
      }
      
      setGoogleStatus(googleRes.data);
      setShopifyStatus(shopifyRes.data);
      setSchedulingConfig(schedulingRes.data);
      setApiIntegrations(apiRes.data || []);
      
      if (toolConfigRes.success && toolConfigRes.data) {
        setToolConfig({
          shopify: toolConfigRes.data.shopify || { enabled: false },
          googleCalendar: toolConfigRes.data.googleCalendar || { enabled: false },
          customApi: toolConfigRes.data.customApi || { enabled: false }
        });
      }
      
      // Load all phone numbers
      if (phonesRes.success && phonesRes.data) {
        const numbers = phonesRes.data.numbers || [];
        setPhoneNumbers(numbers);
        
        // Auto-select first number if available
        if (numbers.length > 0) {
          const firstNumber = numbers[0];
          setSelectedPhoneId(firstNumber.id);
          setSelectedPhone(firstNumber);
          setPhoneSettings({
            businessName: firstNumber.businessName || '',
            welcomeMessage: firstNumber.settings?.welcomeMessage || '',
            systemPrompt: firstNumber.settings?.systemPrompt || '',
            voiceId: firstNumber.settings?.voiceId || '',
            tone: firstNumber.settings?.tone || 'friendly',
            callTransfer: firstNumber.settings?.callTransfer || { enabled: false, phoneNumber: '' }
          });
        } else {
          // No phone numbers - clear selection
          setSelectedPhoneId(null);
          setSelectedPhone(null);
          setPhoneSettings({
            businessName: '',
            welcomeMessage: '',
            systemPrompt: '',
            voiceId: '',
            tone: 'friendly',
            callTransfer: { enabled: false, phoneNumber: '' }
          });
        }
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Select a phone number and load its settings
  const handleSelectPhone = (phone) => {
    setSelectedPhoneId(phone.id);
    setSelectedPhone(phone);
    setPhoneSettings({
      businessName: phone.businessName || '',
      welcomeMessage: phone.settings?.welcomeMessage || '',
      systemPrompt: phone.settings?.systemPrompt || '',
      voiceId: phone.settings?.voiceId || '',
      tone: phone.settings?.tone || 'friendly',
      callTransfer: phone.settings?.callTransfer || { enabled: false, phoneNumber: '' }
    });
  };

  // Save phone-specific settings
  const handleSavePhoneSettings = async () => {
    if (!selectedPhoneId) {
      toast.error('No phone number selected');
      return;
    }
    
    setSavingPhoneSettings(true);
    
    try {
      const res = await fetchApi(`/api/voice/phone-numbers/${selectedPhoneId}/settings`, {
        method: 'PUT',
        body: JSON.stringify(phoneSettings)
      });
      
      if (res.success) {
        toast.success('Phone settings saved successfully!');
        await loadSettings();  // Reload to get updated data
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save phone settings');
    } finally {
      setSavingPhoneSettings(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const res = await fetchApi('/api/voice/settings', {
        method: 'PUT',
        body: JSON.stringify({
          twilioAccountSid: settings.twilioAccountSid,
          twilioAuthToken: settings.twilioAuthToken,
          twilioPhoneNumber: settings.twilioPhoneNumber,
          webhookUrl: settings.webhookUrl,
          webhookSecret: settings.webhookSecret,
          webhookEnabled: settings.webhookEnabled,
          webhookEvents: settings.webhookEvents
        })
      });
      
      if (res.success) {
        toast.success('Settings saved successfully!');
        await loadSettings();
      } else {
        toast.error(res.detail || 'Failed to save settings');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestTwilio = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const res = await fetchApi('/api/voice/settings/test-twilio', {
        method: 'POST',
        body: JSON.stringify({
          accountSid: settings.twilioAccountSid,
          authToken: settings.twilioAuthToken,
          phoneNumber: settings.twilioPhoneNumber
        })
      });
      
      setTestResult(res);
      
      if (res.success) {
        toast.success('Twilio connection verified!');
        setSettings(prev => ({ ...prev, twilioConnected: true }));
      } else {
        toast.error(res.error || 'Twilio connection failed');
      }
    } catch (err) {
      setTestResult({ success: false, error: err.message });
      toast.error(err.message || 'Failed to test Twilio connection');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  // Phone Number Management functions
  const handleSearchNumbers = async () => {
    setSearchingNumbers(true);
    setAvailableNumbers([]);
    
    try {
      const res = await fetchApi('/api/voice/phone-number/search', {
        method: 'POST',
        body: JSON.stringify({
          country: numberSearch.country,
          areaCode: numberSearch.areaCode || null,
          contains: numberSearch.contains || null,
          limit: 10
        })
      });
      
      if (res.success && res.data?.numbers) {
        setAvailableNumbers(res.data.numbers);
        if (res.data.numbers.length === 0) {
          toast.info('No numbers found. Try a different search.');
        }
      }
    } catch (err) {
      toast.error(err.message || 'Failed to search numbers');
    } finally {
      setSearchingNumbers(false);
    }
  };

  const handlePurchaseNumber = async (number) => {
    const businessName = newBusinessName.trim() || `Business ${number.slice(-4)}`;
    
    if (!window.confirm(`Purchase ${number} for $2/month?\n\nBusiness Name: ${businessName}\n\nThis will be added to your monthly bill.`)) {
      return;
    }
    
    setPurchasingNumber(number);
    
    try {
      const res = await fetchApi('/api/voice/phone-number/purchase', {
        method: 'POST',
        body: JSON.stringify({ 
          phoneNumber: number,
          businessName: businessName,
          businessProfileId: currentProfile?.id  // Associate with current business profile
        })
      });
      
      if (res.success) {
        toast.success(`Successfully purchased ${number}!`);
        setAvailableNumbers([]);
        setNewBusinessName('');
        await loadSettings();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to purchase number');
    } finally {
      setPurchasingNumber(false);
    }
  };

  const handleReleaseNumber = async (phoneId) => {
    const phone = phoneNumbers.find(p => p.id === phoneId);
    if (!phone) return;
    
    if (!window.confirm(`Release ${phone.phoneNumber} (${phone.businessName})?\n\nCallers will no longer be able to reach your agent at this number.`)) {
      return;
    }
    
    try {
      await fetchApi(`/api/voice/phone-numbers/${phoneId}`, { method: 'DELETE' });
      toast.success('Phone number released');
      
      // Clear selection if we just released the selected number
      if (selectedPhoneId === phoneId) {
        setSelectedPhoneId(null);
        setSelectedPhone(null);
      }
      
      await loadSettings();
    } catch (err) {
      toast.error(err.message || 'Failed to release number');
    }
  };

  // Call Transfer functions
  const handleSaveCallTransfer = async () => {
    // Validate phone number if enabled
    if (callTransfer.enabled && !callTransfer.phoneNumber) {
      toast.error('Please enter a phone number for call transfers');
      return;
    }
    
    // Basic phone number validation
    if (callTransfer.enabled && callTransfer.phoneNumber) {
      const phoneRegex = /^\+?[1-9]\d{1,14}$/;
      if (!phoneRegex.test(callTransfer.phoneNumber.replace(/[\s\-\(\)]/g, ''))) {
        toast.error('Please enter a valid phone number (E.164 format preferred, e.g., +15551234567)');
        return;
      }
    }
    
    try {
      const res = await fetchApi('/api/voice/settings/call-transfer', {
        method: 'POST',
        body: JSON.stringify(callTransfer)
      });
      
      if (res.success) {
        toast.success('Call transfer settings saved');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save call transfer settings');
    }
  };

  // Google Calendar functions
  const handleConnectGoogle = async () => {
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      const res = await fetchApi(`/api/cloud/integrations/calendar/google/connect${profileParam}`);
      if (res.data?.authUrl) {
        window.location.href = res.data.authUrl;
      }
    } catch (err) {
      toast.error(err.message || 'Failed to initiate Google connection');
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      await fetchApi(`/api/cloud/integrations/calendar/google/disconnect${profileParam}`, { method: 'POST' });
      toast.success('Google Calendar disconnected');
      await loadSettings();
    } catch (err) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  // Shopify functions
  const handleConnectShopify = async () => {
    if (!shopifyForm.storeName) {
      toast.error('Please enter your store name');
      return;
    }
    if (shopifyAuthMode === 'credentials' && (!shopifyForm.clientId || !shopifyForm.clientSecret)) {
      toast.error('Please enter your Client ID and API Secret Key');
      return;
    }

    // OAuth mode — get install URL and redirect
    if (shopifyAuthMode === 'oauth') {
      setConnectingShopify(true);
      try {
        const profileParam = currentProfile?.id ? `&businessProfileId=${currentProfile.id}` : '';
        const res = await fetchApi(
          `/api/shopify/connect-url?shop=${encodeURIComponent(shopifyForm.storeName)}${profileParam}`
        );
        if (res.success && res.data?.installUrl) {
          toast.success('Redirecting to Shopify to authorize...');
          setTimeout(() => { window.location.href = res.data.installUrl; }, 800);
        } else {
          toast.error(res.error || 'Failed to get OAuth URL');
        }
      } catch (err) {
        toast.error(err.message || 'Failed to initiate OAuth');
      } finally {
        setConnectingShopify(false);
      }
      return;
    }

    setConnectingShopify(true);
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      const payload = {
        storeName: shopifyForm.storeName,
        capabilities: ['check_order_status', 'get_tracking_info', 'cancel_order'],
      };
      if (shopifyAuthMode === 'credentials') {
        payload.clientId = shopifyForm.clientId;
        payload.clientSecret = shopifyForm.clientSecret;
      } else {
        payload.adminAccessToken = shopifyForm.adminAccessToken;
      }

      const res = await fetchApi(`/api/cloud/integrations/shopify/connect${profileParam}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (res.success) {
        toast.success('Shopify store connected successfully!');
        setShopifyForm({ storeName: '', clientId: '', clientSecret: '', adminAccessToken: '' });
        await loadSettings();
      } else {
        // Translate app_not_installed into a friendly message
        const rawMsg = res.detail || res.error || '';
        if (rawMsg.toLowerCase().includes('app_not_installed') || rawMsg.toLowerCase().includes('not installed')) {
          toast.error('Your credentials are valid, but the app is not installed. Click "Install app" inside your Shopify Custom App dashboard to activate it.');
        } else {
          toast.error(rawMsg || 'Failed to connect Shopify store');
        }
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('app_not_installed') || msg.toLowerCase().includes('not installed')) {
        toast.error('Your credentials are valid, but the app is not installed. Click "Install app" inside your Shopify Custom App dashboard to activate it.');
      } else {
        toast.error(msg || 'Failed to connect Shopify');
      }
    } finally {
      setConnectingShopify(false);
    }
  };

  const handleDisconnectShopify = async () => {
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      await fetchApi(`/api/cloud/integrations/shopify/disconnect${profileParam}`, { method: 'POST' });
      toast.success('Shopify store disconnected');
      await loadSettings();
    } catch (err) {
      toast.error('Failed to disconnect Shopify');
    }
  };

  const handleUpdateShopifyCapabilities = async (capability, enabled) => {
    if (!shopifyStatus?.connected) return;
    
    const currentCapabilities = shopifyStatus.capabilities || [];
    let newCapabilities;
    
    if (enabled) {
      newCapabilities = [...currentCapabilities, capability];
    } else {
      newCapabilities = currentCapabilities.filter(c => c !== capability);
    }
    
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      await fetchApi(`/api/cloud/integrations/shopify/capabilities${profileParam}`, {
        method: 'PUT',
        body: JSON.stringify({ capabilities: newCapabilities })
      });
      setShopifyStatus(prev => ({ ...prev, capabilities: newCapabilities }));
      toast.success('Capabilities updated');
    } catch (err) {
      toast.error('Failed to update capabilities');
    }
  };

  // Scheduling functions
  const handleSaveScheduling = async (updates) => {
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      await fetchApi(`/api/cloud/integrations/scheduling/config${profileParam}`, {
        method: 'POST',
        body: JSON.stringify(updates)
      });
      toast.success('Scheduling settings saved');
      await loadSettings();
    } catch (err) {
      toast.error('Failed to save scheduling settings');
    }
  };

  // Custom API functions
  const handleAddApi = async () => {
    if (!newApi.name || !newApi.variableName || !newApi.url) {
      toast.error('Please fill in name, variable name, and URL');
      return;
    }
    
    try {
      let headers = {};
      if (newApi.headers) {
        try {
          headers = JSON.parse(newApi.headers);
        } catch {
          toast.error('Invalid JSON in headers');
          return;
        }
      }
      
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      await fetchApi(`/api/cloud/integrations/api-integrations${profileParam}`, {
        method: 'POST',
        body: JSON.stringify({
          name: newApi.name,
          variableName: newApi.variableName,
          method: newApi.method,
          url: newApi.url,
          headers,
          body: newApi.body || null
        })
      });
      
      toast.success('API integration added');
      setShowAddApiModal(false);
      setNewApi({ name: '', variableName: '', method: 'GET', url: '', headers: '', body: '' });
      await loadSettings();
    } catch (err) {
      toast.error('Failed to add API integration');
    }
  };

  const handleTestApi = async (id, variableName) => {
    setTestingApiId(id);
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      const res = await fetchApi(`/api/cloud/integrations/api-integrations/call${profileParam}`, {
        method: 'POST',
        body: JSON.stringify({ variableName, context: {} })
      });
      
      if (res.success) {
        toast.success('API call successful!');
      } else {
        toast.error(`API call failed: ${res.error}`);
      }
    } catch (err) {
      toast.error('Test failed');
    } finally {
      setTestingApiId(null);
    }
  };

  const handleDeleteApi = async (id) => {
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      await fetchApi(`/api/cloud/integrations/api-integrations/${id}${profileParam}`, { method: 'DELETE' });
      toast.success('API integration deleted');
      await loadSettings();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Voice Agent Tool Config - Toggle AI access
  const handleToggleAiAccess = async (integration, enabled) => {
    const updates = {
      [integration]: { enabled }
    };
    
    try {
      const profileParam = currentProfile?.id ? `?businessProfileId=${currentProfile.id}` : '';
      await fetchApi(`/api/cloud/integrations/voice-agent/tools/config${profileParam}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      
      setToolConfig(prev => ({
        ...prev,
        [integration]: { ...prev[integration], enabled }
      }));
      
      toast.success(`AI Agent ${enabled ? 'can now access' : 'no longer has access to'} ${integration === 'googleCalendar' ? 'Google Calendar' : integration === 'customApi' ? 'Custom APIs' : 'Shopify'}`);
    } catch (err) {
      toast.error('Failed to update AI access');
    }
  };

  const webhookUrl = `${window.location.origin}/api/voice/twilio/inbound`;
  const statusCallbackUrl = `${window.location.origin}/api/voice/twilio/status`;

  if (profileLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Building2 className="w-12 h-12 text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Business Profile Selected</h3>
        <p className="text-gray-400">Select a business profile from the dropdown above to configure settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="voice-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-400" />
            Settings for {currentProfile.name}
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Configure integrations and webhooks for this business
          </p>
        </div>
        {activeTab === 'twilio' && (
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-800 pb-2">
        <button
          onClick={() => setActiveTab('twilio')}
          className={`px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'twilio' 
              ? 'bg-gray-800 text-white border-b-2 border-purple-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Phone className="w-4 h-4 inline mr-2" />
          Phone Number
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          className={`px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'integrations' 
              ? 'bg-gray-800 text-white border-b-2 border-purple-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <ShoppingBag className="w-4 h-4 inline mr-2" />
          Integrations
        </button>
        <button
          onClick={() => setActiveTab('webhooks')}
          className={`px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'webhooks' 
              ? 'bg-gray-800 text-white border-b-2 border-purple-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Webhook className="w-4 h-4 inline mr-2" />
          Webhooks
        </button>
        <button
          onClick={() => setActiveTab('scheduling')}
          className={`px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'scheduling' 
              ? 'bg-gray-800 text-white border-b-2 border-purple-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-2" />
          Scheduling
        </button>
        <button
          onClick={() => setActiveTab('api')}
          className={`px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'api' 
              ? 'bg-gray-800 text-white border-b-2 border-purple-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Link2 className="w-4 h-4 inline mr-2" />
          Custom API
        </button>
      </div>

      {/* Phone Number Tab */}
      {activeTab === 'twilio' && (
        <div className="space-y-6">
          {/* Phone Numbers List */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Phone className="w-5 h-5 text-green-400" />
                Your Phone Numbers
              </CardTitle>
              <CardDescription className="text-gray-400">
                Phone numbers assigned to this business profile
              </CardDescription>
            </CardHeader>
            <CardContent>
              {phoneNumbers.length > 0 ? (
                <div className="space-y-3">
                  {phoneNumbers.map((phone) => (
                    <div
                      key={phone.id}
                      className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                        selectedPhoneId === phone.id 
                          ? 'bg-purple-900/30 border border-purple-500/50' 
                          : 'bg-gray-800 hover:bg-gray-700/50'
                      }`}
                      onClick={() => handleSelectPhone(phone)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-3 h-3 rounded-full bg-green-400" />
                        <div>
                          <div className="font-mono text-white text-lg">{phone.phoneNumber}</div>
                          <div className="text-sm text-gray-400">{phone.businessName}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-2 text-green-400 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Active
                          </div>
                          <div className="text-xs text-gray-500">${phone.monthlyPrice?.toFixed(2)}/mo</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleReleaseNumber(phone.id); }}
                          className="text-red-400 hover:bg-red-400/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Phone className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 mb-2">No phone numbers yet</p>
                  <p className="text-sm text-gray-500">Search and purchase a phone number below</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phone Number Settings (Call Transfer) - Only show when a phone is selected */}
          {selectedPhone && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <PhoneForwarded className="w-5 h-5 text-amber-400" />
                  Call Transfer Settings
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Configure call forwarding for {selectedPhone.phoneNumber}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                  <div>
                    <div className="text-white font-medium">Enable Call Transfer</div>
                    <div className="text-sm text-gray-400">
                      Allow the AI agent to transfer calls to a human operator
                    </div>
                  </div>
                  <Switch
                    checked={phoneSettings.callTransfer?.enabled || false}
                    onCheckedChange={(checked) => setPhoneSettings({
                      ...phoneSettings,
                      callTransfer: { ...phoneSettings.callTransfer, enabled: checked }
                    })}
                    className="data-[state=checked]:bg-green-500"
                  />
                </div>

                {phoneSettings.callTransfer?.enabled && (
                  <div>
                    <Label className="text-gray-300">Transfer Phone Number</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        type="tel"
                        value={phoneSettings.callTransfer?.phoneNumber || ''}
                        onChange={(e) => setPhoneSettings({
                          ...phoneSettings,
                          callTransfer: { ...phoneSettings.callTransfer, phoneNumber: e.target.value }
                        })}
                        placeholder="+1 (555) 123-4567"
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the phone number where calls should be transferred when requested
                    </p>
                  </div>
                )}

                <div className="pt-4 flex justify-end">
                  <Button
                    onClick={handleSavePhoneSettings}
                    disabled={savingPhoneSettings}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {savingPhoneSettings ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search & Purchase Phone Numbers - Only show if no phone numbers exist */}
          {phoneNumbers.length === 0 && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-400" />
                  Get a New Phone Number
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Search for available Twilio phone numbers to purchase ($2/month)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search Form */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-gray-300">Country</Label>
                    <select
                      value={numberSearch.country}
                      onChange={(e) => setNumberSearch({ ...numberSearch, country: e.target.value })}
                      className="w-full mt-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="US">United States (+1)</option>
                      <option value="CA">Canada (+1)</option>
                      <option value="GB">United Kingdom (+44)</option>
                      <option value="AU">Australia (+61)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Area Code (optional)</Label>
                    <Input
                      type="text"
                      value={numberSearch.areaCode}
                      onChange={(e) => setNumberSearch({ ...numberSearch, areaCode: e.target.value })}
                      placeholder="415"
                      maxLength={3}
                      className="mt-1 bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Contains (optional)</Label>
                    <Input
                      type="text"
                      value={numberSearch.contains}
                      onChange={(e) => setNumberSearch({ ...numberSearch, contains: e.target.value })}
                      placeholder="PIZZA"
                      maxLength={7}
                      className="mt-1 bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleSearchNumbers}
                      disabled={searchingNumbers}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {searchingNumbers ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Search Numbers
                    </Button>
                  </div>
                </div>

                {/* Available Numbers List */}
                {availableNumbers.length > 0 && (
                  <div className="space-y-3 mt-4">
                    <div>
                      <Label className="text-gray-300">Business Name for this number</Label>
                      <Input
                        type="text"
                        value={newBusinessName}
                        onChange={(e) => setNewBusinessName(e.target.value)}
                        placeholder="e.g., Main Line, Support, Sales"
                        className="mt-1 bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    
                    <div className="text-sm text-gray-400 mt-4">
                      Found {availableNumbers.length} available numbers:
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {availableNumbers.map((number, idx) => (
                        <div
                          key={number.phoneNumber || idx}
                          className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-colors"
                        >
                          <div>
                            <div className="font-mono text-white">{number.phoneNumber || number}</div>
                            {number.locality && (
                              <div className="text-xs text-gray-400">
                                {number.locality}, {number.region}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handlePurchaseNumber(number.phoneNumber || number)}
                            disabled={purchasingNumber === (number.phoneNumber || number)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {purchasingNumber === (number.phoneNumber || number) ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-1" />
                                ${number.monthlyPrice?.toFixed(2) || '2.00'}/mo
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {/* Google Calendar */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                Google Calendar
              </CardTitle>
              <CardDescription className="text-gray-400">
                Connect to sync availability and create calendar events for bookings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  {googleStatus?.connected ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400">Connected</span>
                      {googleStatus.email && (
                        <span className="text-gray-400">({googleStatus.email})</span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-400">Not connected</span>
                    </div>
                  )}
                </div>
                {googleStatus?.connected ? (
                  <Button
                    variant="outline"
                    onClick={handleDisconnectGoogle}
                    className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    onClick={handleConnectGoogle}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="connect-google-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect Google Calendar
                  </Button>
                )}
              </div>
              
              {/* AI Agent Access Toggle */}
              {googleStatus?.connected && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-medium text-white">AI Agent Access</span>
                      <p className="text-xs text-gray-500">Allow voice agent to check availability and schedule appointments</p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={toolConfig.googleCalendar?.enabled || false}
                        onChange={(e) => handleToggleAiAccess('googleCalendar', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </div>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shopify */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-green-400" />
                Shopify Store
              </CardTitle>
              <CardDescription className="text-gray-400">
                Connect your Shopify store to enable order management via voice agent.
                <a
                  href="https://help.shopify.com/en/manual/apps/app-types/custom-apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300 ml-1 inline-flex items-center gap-1"
                >
                  Custom App setup guide <ExternalLink className="w-3 h-3" />
                </a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {shopifyStatus?.connected ? (
                <>
                  <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <div>
                        <span className="text-green-400 font-medium">Connected</span>
                        <span className="text-gray-400 ml-2">to {shopifyStatus.storeName}.myshopify.com</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleDisconnectShopify}
                      className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                    >
                      Disconnect
                    </Button>
                  </div>

                  {/* Capabilities */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Voice Agent Capabilities
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Select what actions the AI voice agent can perform on Shopify orders
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: 'check_order_status', label: 'Check Order Status', icon: Search, description: 'Look up order details by order number' },
                        { id: 'get_tracking_info', label: 'Get Tracking Info', icon: Truck, description: 'Retrieve shipment tracking information' },
                        { id: 'cancel_order', label: 'Cancel Orders', icon: Ban, description: 'Cancel unfulfilled orders on request' }
                      ].map(cap => (
                        <label 
                          key={cap.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            (shopifyStatus.capabilities || []).includes(cap.id)
                              ? 'bg-purple-500/10 border-purple-500/30'
                              : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={(shopifyStatus.capabilities || []).includes(cap.id)}
                            onChange={(e) => handleUpdateShopifyCapabilities(cap.id, e.target.checked)}
                            className="mt-1 w-4 h-4 rounded border-gray-700 bg-gray-800 text-purple-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <cap.icon className="w-4 h-4 text-gray-400" />
                              <span className="text-white font-medium text-sm">{cap.label}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{cap.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* AI Agent Access Toggle */}
                  <div className="pt-4 border-t border-gray-800">
                    <label className="flex items-center justify-between cursor-pointer">
                      <div>
                        <span className="text-sm font-medium text-white">AI Agent Access</span>
                        <p className="text-xs text-gray-500">Allow voice agent to access Shopify order data during calls</p>
                      </div>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={toolConfig.shopify?.enabled || false}
                          onChange={(e) => handleToggleAiAccess('shopify', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </div>
                    </label>

                    {/* Identity Verification Notice */}
                    {toolConfig.shopify?.enabled && (
                      <div className="mt-3 bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-blue-300 space-y-1">
                            <p className="font-medium text-blue-200">Identity verification is enabled by default</p>
                            <p className="text-blue-300/70">Before sharing any order details, the AI agent will always ask the caller to confirm:</p>
                            <ul className="space-y-0.5 text-blue-300/60 list-disc list-inside">
                              <li>Their <strong className="text-blue-300">order number</strong></li>
                              <li>Their <strong className="text-blue-300">last name</strong> on the account</li>
                              <li>The <strong className="text-blue-300">last 4 digits</strong> of the phone number on the account</li>
                            </ul>
                            <p className="text-blue-300/50 pt-1">This ensures order information is only shared with the correct customer.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      {/* Store Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Store Name</label>
                        <div className="flex items-center">
                          <input
                            type="text"
                            value={shopifyForm.storeName}
                            onChange={(e) => setShopifyForm({ ...shopifyForm, storeName: e.target.value })}
                            placeholder="your-store"
                            className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                          <span className="px-3 py-2 bg-gray-700 border border-l-0 border-gray-700 rounded-r-lg text-gray-400 text-sm">
                            .myshopify.com
                          </span>
                        </div>
                      </div>

                      {/* Auth mode toggle — 2 options only */}
                      <div className="flex rounded-lg overflow-hidden border border-gray-700 text-sm">
                        <button
                          onClick={() => setShopifyAuthMode('credentials')}
                          className={`flex-1 py-2 px-3 transition-colors ${shopifyAuthMode === 'credentials' ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-300'}`}
                        >
                          Client ID + Secret
                        </button>
                        <button
                          onClick={() => setShopifyAuthMode('oauth')}
                          className={`flex-1 py-2 px-3 transition-colors ${shopifyAuthMode === 'oauth' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-gray-300'}`}
                        >
                          OAuth ✦ Recommended
                        </button>
                      </div>

                      {/* Client ID + Secret mode */}
                      {shopifyAuthMode === 'credentials' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Client ID</label>
                            <input
                              type="text"
                              value={shopifyForm.clientId}
                              onChange={(e) => setShopifyForm({ ...shopifyForm, clientId: e.target.value })}
                              placeholder="Shopify Custom App Client ID"
                              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-purple-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">API Secret Key</label>
                            <div className="relative">
                              <input
                                type={showShopifySecret ? 'text' : 'password'}
                                value={shopifyForm.clientSecret}
                                onChange={(e) => setShopifyForm({ ...shopifyForm, clientSecret: e.target.value })}
                                placeholder="shpss_xxxxxxxxxxxxxxxxxxxx"
                                className="w-full px-4 py-2 pr-10 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-purple-500"
                              />
                              <button
                                type="button"
                                onClick={() => setShowShopifySecret(!showShopifySecret)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                              >
                                {showShopifySecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Found in Shopify Partner Dashboard → Apps → your app → API credentials</p>
                          </div>
                        </>
                      )}

                      {/* OAuth mode */}
                      {shopifyAuthMode === 'oauth' && (
                        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-sm text-green-300 space-y-1">
                          <p className="font-medium">Shopify OAuth (Authorization Code flow)</p>
                          <p className="text-green-400/70 text-xs">You'll be redirected to Shopify to authorize the app. The access token is obtained and saved automatically — no manual copying needed.</p>
                          <p className="text-green-400/60 text-xs pt-1">Note: OAuth does not create the Shopify app for you. You need an existing Custom App in your Shopify Partner Dashboard first.</p>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={handleConnectShopify}
                      disabled={connectingShopify || !shopifyForm.storeName || (shopifyAuthMode === 'credentials' ? (!shopifyForm.clientId || !shopifyForm.clientSecret) : false)}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="connect-shopify-btn"
                    >
                      {connectingShopify ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ShoppingBag className="w-4 h-4 mr-2" />
                      )}
                      {shopifyAuthMode === 'oauth' ? 'Install App via Shopify OAuth' : 'Connect Shopify Store'}
                    </Button>

                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-amber-300 space-y-2 w-full">
                        <p className="font-medium">Required Shopify Custom App setup</p>

                        <div>
                          <p className="text-amber-400/70 mb-1">1. API Scopes (set in your Shopify Custom App):</p>
                          <code className="bg-gray-800 px-2 py-0.5 rounded text-amber-200">read_orders, write_orders, read_fulfillments</code>
                        </div>

                        <div>
                          <p className="text-amber-400/70 mb-1">2. App URL (set in Shopify Partner Dashboard → App setup):</p>
                          <code className="bg-gray-800 px-2 py-1 rounded text-amber-200 block break-all select-all">https://api.ruonn.com/api/shopify/install</code>
                        </div>

                        <div>
                          <p className="text-amber-400/70 mb-1">3. Allowed Redirect URL (must be exact):</p>
                          <code className="bg-gray-800 px-2 py-1 rounded text-amber-200 block break-all select-all">https://api.ruonn.com/api/shopify/callback</code>
                        </div>

                        <p className="text-amber-200/60 pt-1">After configuring, click <strong>Install app</strong> in your Shopify settings, then come back and connect.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Webhook className="w-5 h-5 text-green-400" />
              CRM Webhook Integration
            </CardTitle>
            <CardDescription className="text-gray-400">
              Send call events to your CRM or backend system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="webhookEnabled"
                checked={settings.webhookEnabled}
                onChange={(e) => setSettings({ ...settings, webhookEnabled: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <label htmlFor="webhookEnabled" className="text-sm text-gray-300">
                Enable webhook notifications
              </label>
            </div>
            
            {settings.webhookEnabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Webhook URL</label>
                  <input
                    type="url"
                    value={settings.webhookUrl}
                    onChange={(e) => setSettings({ ...settings, webhookUrl: e.target.value })}
                    placeholder="https://your-crm.com/webhook/calls"
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Webhook Secret (for signature verification)</label>
                  <input
                    type="password"
                    value={settings.webhookSecret}
                    onChange={(e) => setSettings({ ...settings, webhookSecret: e.target.value })}
                    placeholder="Optional secret for HMAC verification"
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Events to send</label>
                  <div className="flex flex-wrap gap-3">
                    {['call.started', 'call.completed', 'call.failed', 'transcript.ready'].map((event) => (
                      <label key={event} className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={settings.webhookEvents?.includes(event) || false}
                          onChange={(e) => {
                            const events = new Set(settings.webhookEvents || []);
                            if (e.target.checked) {
                              events.add(event);
                            } else {
                              events.delete(event);
                            }
                            setSettings({ ...settings, webhookEvents: Array.from(events) });
                          }}
                          className="w-4 h-4 rounded"
                        />
                        <code className="text-xs bg-gray-800 px-2 py-0.5 rounded">{event}</code>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-800/50 rounded-lg p-3 mt-2">
                  <p className="text-xs text-gray-400">
                    Webhook payload example:
                  </p>
                  <pre className="text-xs text-gray-300 mt-2 overflow-x-auto">
{`{
  "event": "call.completed",
  "callId": "CA123...",
  "from": "+1234567890",
  "duration": 120,
  "transcript": "...",
  "timestamp": "2026-03-05T12:00:00Z"
}`}
                  </pre>
                </div>
              </>
            )}
            
            <div className="pt-4 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Webhook Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduling Tab */}
      {activeTab === 'scheduling' && (
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Scheduling Settings
            </CardTitle>
            <CardDescription className="text-gray-400">
              Configure availability for calendar bookings via voice agent
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {schedulingConfig ? (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Timezone
                    </label>
                    <select
                      value={schedulingConfig.timezone || 'America/New_York'}
                      onChange={(e) => handleSaveScheduling({ timezone: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="UTC">UTC</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Slot Duration (min)
                    </label>
                    <select
                      value={schedulingConfig.slotDurationMins || 30}
                      onChange={(e) => handleSaveScheduling({ slotDurationMins: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Min Notice (hours)
                    </label>
                    <select
                      value={schedulingConfig.minNoticeHours || 2}
                      onChange={(e) => handleSaveScheduling({ minNoticeHours: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="1">1 hour</option>
                      <option value="2">2 hours</option>
                      <option value="4">4 hours</option>
                      <option value="24">24 hours</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Working Hours
                  </label>
                  <div className="space-y-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                      const workingHours = schedulingConfig.workingHours || [];
                      const wh = workingHours.find(w => w.day === idx) || {
                        day: idx,
                        enabled: idx < 5,
                        start: '09:00',
                        end: '17:00'
                      };
                      return (
                        <div key={day} className="flex items-center gap-4 p-2 bg-gray-800/50 rounded-lg">
                          <label className="flex items-center gap-2 w-24">
                            <input
                              type="checkbox"
                              checked={wh.enabled}
                              onChange={(e) => {
                                const updated = (schedulingConfig.workingHours || []).map(w =>
                                  w.day === idx ? { ...w, enabled: e.target.checked } : w
                                );
                                if (!updated.find(w => w.day === idx)) {
                                  updated.push({ ...wh, enabled: e.target.checked });
                                }
                                handleSaveScheduling({ workingHours: updated });
                              }}
                              className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-purple-500"
                            />
                            <span className="text-gray-300">{day}</span>
                          </label>
                          <input
                            type="time"
                            value={wh.start}
                            onChange={(e) => {
                              const updated = (schedulingConfig.workingHours || []).map(w =>
                                w.day === idx ? { ...w, start: e.target.value } : w
                              );
                              handleSaveScheduling({ workingHours: updated });
                            }}
                            disabled={!wh.enabled}
                            className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-white disabled:opacity-50"
                          />
                          <span className="text-gray-500">to</span>
                          <input
                            type="time"
                            value={wh.end}
                            onChange={(e) => {
                              const updated = (schedulingConfig.workingHours || []).map(w =>
                                w.day === idx ? { ...w, end: e.target.value } : w
                              );
                              handleSaveScheduling({ workingHours: updated });
                            }}
                            disabled={!wh.enabled}
                            className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-white disabled:opacity-50"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Buffer Before (min)
                    </label>
                    <input
                      type="number"
                      value={schedulingConfig.bufferBeforeMins || 0}
                      onChange={(e) => handleSaveScheduling({ bufferBeforeMins: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      min="0"
                      max="60"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Buffer After (min)
                    </label>
                    <input
                      type="number"
                      value={schedulingConfig.bufferAfterMins || 0}
                      onChange={(e) => handleSaveScheduling({ bufferAfterMins: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      min="0"
                      max="60"
                    />
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-400">Loading scheduling configuration...</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Custom API Tab */}
      {activeTab === 'api' && (
        <div className="space-y-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-purple-400" />
                  Custom API Integrations
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Connect your own APIs. Use variable references in prompts like{' '}
                  <code className="bg-gray-800 px-2 py-1 rounded text-purple-400">{'{order_status}'}</code>
                </CardDescription>
              </div>
              <Button
                onClick={() => setShowAddApiModal(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add API
              </Button>
            </CardHeader>
            <CardContent>
              {apiIntegrations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No API integrations configured yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {apiIntegrations.map((api) => (
                    <div
                      key={api._id || api.id}
                      className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{api.name}</span>
                          <code className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                            {'{' + api.variableName + '}'}
                          </code>
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          <span className="text-amber-400">{api.method}</span> {api.url}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestApi(api._id || api.id, api.variableName)}
                          disabled={testingApiId === (api._id || api.id)}
                        >
                          {testingApiId === (api._id || api.id) ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteApi(api._id || api.id)}
                          className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* AI Agent Access Toggle */}
              {apiIntegrations.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-medium text-white">AI Agent Access</span>
                      <p className="text-xs text-gray-500">Allow voice agent to call these APIs during conversations</p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={toolConfig.customApi?.enabled || false}
                        onChange={(e) => handleToggleAiAccess('customApi', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </div>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add API Modal */}
          {showAddApiModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="bg-gray-900 border-gray-700 w-full max-w-lg mx-4">
                <CardHeader>
                  <CardTitle className="text-white">Add API Integration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                    <input
                      type="text"
                      value={newApi.name}
                      onChange={(e) => setNewApi({ ...newApi, name: e.target.value })}
                      placeholder="Order Status API"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Variable Name</label>
                    <input
                      type="text"
                      value={newApi.variableName}
                      onChange={(e) => setNewApi({ ...newApi, variableName: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                      placeholder="order_status"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use this in prompts: {'{' + (newApi.variableName || 'variable_name') + '}'}</p>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Method</label>
                      <select
                        value={newApi.method}
                        onChange={(e) => setNewApi({ ...newApi, method: e.target.value })}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                      </select>
                    </div>
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-300 mb-1">URL</label>
                      <input
                        type="text"
                        value={newApi.url}
                        onChange={(e) => setNewApi({ ...newApi, url: e.target.value })}
                        placeholder="https://api.example.com/orders/{order_id}"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Headers (JSON)</label>
                    <textarea
                      value={newApi.headers}
                      onChange={(e) => setNewApi({ ...newApi, headers: e.target.value })}
                      placeholder='{"Authorization": "Bearer xxx"}'
                      rows={2}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm"
                    />
                  </div>
                  {newApi.method !== 'GET' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Body (JSON)</label>
                      <textarea
                        value={newApi.body}
                        onChange={(e) => setNewApi({ ...newApi, body: e.target.value })}
                        placeholder='{"key": "value"}'
                        rows={2}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm"
                      />
                    </div>
                  )}
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddApiModal(false);
                        setNewApi({ name: '', variableName: '', method: 'GET', url: '', headers: '', body: '' });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddApi}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Add Integration
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
